/**
 * ArchLang v0.10 — policy engine, slice 1 (P1b-3): state-rule findings.
 *
 * Evaluates a `PolicyDecl`'s state rules (`forbid` / `require`) over the model +
 * derived graph into a flat list of {@link Finding}s, then applies `except`
 * waivers. Findings carry the policy's severity (from its header `severity:`
 * field, default `error`). This is where the governance grammar first *bites*.
 *
 * `escalate <PolicyRef> to <severity>` is a **cross-policy, scope-level** raise:
 * it lives in one policy's body but retargets another policy's findings, so it is
 * applied by {@link evalPolicies} over the whole set, not by the single-policy
 * {@link evalPolicy}. Raise-only (monotone): a lowering escalate is a no-op here
 * (rejected as a lint in a later slice). Escalate targets that name a built-in
 * diagnostic code (SCREAMING_SNAKE) rather than a policy in the set are inert —
 * those findings come from validation, not this engine.
 *
 * Scope intersection (v0.10 §5.2): a module-scoped policy (`ownerPath` set —
 * module-body or `in <Module>`-placed, collected by `collectScopedPolicies`)
 * restricts findings to the owning module's SUBTREE (edges by source endpoint)
 * and resolves selector names from the module context first.
 *
 * DEFERRED to later slices: `when` gates (change policies — need a base→head
 * diff), cross-package `use policy`, the lowering-escalate lint, diagnostic-code
 * escalation. A rule that uses a not-yet-evaluated selector feature propagates the
 * evaluator's `UnsupportedSelectorError`.
 */

import type { Diagnostic, DiagnosticSeverity, SourceSpan } from "./types.js";
import type { DependencyEdge } from "./graph.js";
import type { PolicyDecl, ExceptRule, EscalateRule } from "./v010-ast.js";
import type { Selector, Expr } from "./selector-ast.js";
import { evalSelector, type EvalContext } from "./selector-eval.js";

export type Severity = string; // `error` | `warning` | `advisory` (validated in a later slice)

/** Severity rank for the raise-only escalate lattice (`advisory < warning < error`). */
const SEVERITY_RANK: Record<string, number> = { advisory: 0, warning: 1, error: 2 };
function rankOf(s: Severity): number {
  return SEVERITY_RANK[s] ?? SEVERITY_RANK.error!; // unknown severities behave as `error` (fail-closed)
}
/** The higher of two severities (raise-only lattice join). */
export function maxSeverity(a: Severity, b: Severity): Severity {
  return rankOf(b) > rankOf(a) ? b : a;
}

/** One policy finding — a node or an edge that violates a state rule. */
export interface Finding {
  readonly policy: string;
  readonly ruleKind: "forbid" | "require";
  readonly sort: "node" | "edge";
  /** Stable display key: a node path, or `<source> > <targetInterface>` for edges. */
  readonly anchorKey: string;
  /** The offending edge (edge-sort findings only). */
  readonly edge?: DependencyEdge;
  readonly severity: Severity;
  readonly waived: boolean;
  readonly waiverReason?: string;
  /** The RULE's span (the `forbid`/`require` line in the policy source). */
  readonly source?: SourceSpan;
  /** The VIOLATION site — the offending element's decl / the edge's call site.
   *  This is where a diagnostic anchors in the editor (falls back to `source`). */
  readonly anchorSource?: SourceSpan;
}

/**
 * Evaluate a policy's state rules into findings (waivers applied).
 *
 * `now` is the **gate clock** (§4): time-anchored gate executions (CLI
 * `archlang check`, Studio accept-gate) pass the host clock so an `except …
 * { until: <date> }` past its date **expires** — the waiver stops suppressing
 * and its finding reactivates. Timeless surfaces (render / LSP diagnostics /
 * `violating` selection) pass **no clock**, so waivers never expire there — a
 * model green yesterday stays green today with zero diff.
 */
export function evalPolicy(policy: PolicyDecl, outerCtx: EvalContext, now?: Date): Finding[] {
  return applyWaivers(evalPolicyFindings(policy, outerCtx), policy, policyEvalCtx(policy, outerCtx), now);
}

/**
 * A policy's RAW state-rule findings — no waivers applied. The head-state
 * surface ({@link evalPolicy}) layers `applyWaivers` on top; the gate surface
 * (`gate-waivers.ts`) layers the two-arm effectiveness rule instead (design
 * §10 "Waiver effectiveness at gates").
 */
export function evalPolicyFindings(policy: PolicyDecl, outerCtx: EvalContext): Finding[] {
  const severity = severityOf(policy);
  const raw: Finding[] = [];

  // §4 scope = the declaration scope's SUBTREE: a module-scoped policy
  // (`ownerPath` stamped by `collectScopedPolicies`) resolves selector names
  // from its module context and intersects subjects with the scope — node
  // subjects by subtree membership (the owner itself included), edge subjects
  // by their SOURCE endpoint (consistent with finding anchoring). An unscoped
  // (top-level) policy is byte-identical to pre-scoping behavior.
  const ctx = policyEvalCtx(policy, outerCtx);
  const scope = policy.ownerPath && policy.ownerPath.length > 0 ? policy.ownerPath.join(".") : undefined;
  const inScopeKey = (key: string): boolean => !scope || key === scope || key.startsWith(scope + ".");
  const edgeInScope = (e: DependencyEdge): boolean =>
    !scope || inScopeKey(e.kind === "call" ? e.fromModulePath.join(".") : e.fromProcessName ?? "");

  for (const rule of policy.rules) {
    if (rule.kind === "forbid") {
      const r = evalSelector(rule.selector, ctx);
      if (r.sort === "node") {
        for (const key of r.nodes) {
          if (!inScopeKey(key)) continue;
          raw.push({ policy: policy.name, ruleKind: "forbid", sort: "node", anchorKey: key, severity, waived: false, source: rule.source, anchorSource: ctx.universe.byKey(key)?.source });
        }
      } else {
        for (const e of r.edges) {
          if (!edgeInScope(e)) continue;
          raw.push({ policy: policy.name, ruleKind: "forbid", sort: "edge", anchorKey: edgeKey(e), edge: e, severity, waived: false, source: rule.source, anchorSource: e.span });
        }
      }
    } else if (rule.kind === "require") {
      const subjects = evalSelector(rule.subject, ctx);
      if (subjects.sort !== "node") continue; // a require subject is node-sort (validated later)
      for (const key of subjects.nodes) {
        if (!inScopeKey(key)) continue;
        const subject = ctx.universe.byKey(key);
        if (!subject) continue;
        const obligation = evalSelector(rule.obligation, { ...ctx, subject });
        const holds = obligation.sort === "node" ? obligation.nodes.size > 0 : obligation.edges.size > 0;
        if (!holds) {
          raw.push({ policy: policy.name, ruleKind: "require", sort: "node", anchorKey: key, severity, waived: false, source: rule.source, anchorSource: subject.source });
        }
      }
    }
    // escalate / when — deferred slices.
  }

  return raw;
}

/** The evaluation context a policy's selectors run in — a module-scoped policy
 *  resolves names from its owner chain first (v0.10 §5.2, the §5.5 precedent).
 *  Exported for the gate-waiver evaluator, which must resolve `except`
 *  selectors in exactly this context on BOTH diff sides. */
export function policyEvalCtx(policy: PolicyDecl, ctx: EvalContext): EvalContext {
  return policy.ownerPath && policy.ownerPath.length > 0 ? { ...ctx, scopePath: policy.ownerPath } : ctx;
}

/**
 * Waive findings whose anchor is matched by a same-sort `except` selector.
 *
 * At the gate (a `now` clock is supplied) an `except` whose `until:` field
 * names a date in the past is **expired** and skipped, so its finding stays
 * active. With no clock (timeless), every matched `except` waives regardless of
 * `until:`.
 */
function applyWaivers(findings: Finding[], policy: PolicyDecl, ctx: EvalContext, now?: Date): Finding[] {
  const excepts = policy.rules.filter((r): r is ExceptRule => r.kind === "except" && !isWaiverExpired(r, now));
  if (excepts.length === 0) return findings;

  const nodeWaivers: { keys: Set<string>; reason: string }[] = [];
  const edgeWaivers: { edges: Set<DependencyEdge>; reason: string }[] = [];
  for (const ex of excepts) {
    const r = evalSelector(ex.selector, ctx);
    if (r.sort === "node") nodeWaivers.push({ keys: r.nodes, reason: ex.reason });
    else edgeWaivers.push({ edges: r.edges, reason: ex.reason });
  }

  return findings.map((f) => {
    if (f.sort === "node") {
      const w = nodeWaivers.find((w) => w.keys.has(f.anchorKey));
      return w ? { ...f, waived: true, waiverReason: w.reason } : f;
    }
    // Edge waiver: the SAME edge object survives both evaluations (graph.edges).
    const w = f.edge && edgeWaivers.find((w) => w.edges.has(f.edge!));
    return w ? { ...f, waived: true, waiverReason: w.reason } : f;
  });
}

/**
 * Whether a waiver has expired relative to the gate clock. Timeless (no clock)
 * → never expired. Otherwise the `except`'s `until:` field must parse to a
 * valid date **strictly before** `now`. An absent or unparseable `until:` never
 * expires (a bounded promise the language merely records — malformed bounds are
 * a lint concern, not a silent reactivation).
 */
export function isWaiverExpired(ex: ExceptRule, now: Date | undefined): boolean {
  if (!now) return false;
  const until = waiverUntil(ex);
  return until !== undefined && until.getTime() < now.getTime();
}

/** The `until:` field of an `except` body parsed to a Date, if present + valid. */
function waiverUntil(ex: ExceptRule): Date | undefined {
  const field = ex.fields.find((f) => f.path.join(".") === "until");
  const v = field?.value;
  const text = v && (v.kind === "string" || v.kind === "identifier") ? v.text : undefined;
  if (!text) return undefined;
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** The policy's declared severity (`severity:` header field), default `error`. */
function severityOf(policy: PolicyDecl): Severity {
  const f = policy.fields.find((f) => f.path.join(".") === "severity");
  const v = f?.value;
  if (v && (v.kind === "identifier" || v.kind === "string")) return v.text;
  return "error";
}

function edgeKey(e: DependencyEdge): string {
  return e.kind === "call"
    ? `${e.fromModulePath.join(".")} > ${e.toInterfacePath.join(".")}`
    : `${e.fromProcessName ?? e.fromProcessId} do ${e.toProcessName ?? e.toProcessId}`;
}

/** Active (non-waived) findings — the `violating <P>` set / gate-failing set. */
export function activeFindings(findings: readonly Finding[]): Finding[] {
  return findings.filter((f) => !f.waived);
}

/**
 * Evaluate a whole set of policies, then apply cross-policy `escalate` raises.
 *
 * An `escalate <PolicyRef> to <sev>` in ANY policy's body raises the severity of
 * every finding produced by the named policy (raise-only — a lower `to` is a
 * no-op). Multiple escalations of one target take the highest. Targets that name
 * no policy in the set are inert here (built-in diagnostic codes escalate in the
 * validation layer). Waivers are already applied per policy by {@link evalPolicy}.
 *
 * `now` is the optional gate clock threaded to {@link evalPolicy} for `until:`
 * waiver expiry — the CLI/Studio gate passes the host clock; the timeless
 * diagnostic path ({@link policyDiagnostics}) omits it.
 */
export interface EvalPoliciesOptions {
  /**
   * Called when a policy throws during evaluation (e.g. it uses a not-yet-
   * supported selector/expression feature — {@link UnsupportedSelectorError}).
   * When provided, that policy is SKIPPED (its findings omitted) and the rest
   * still evaluate; escalations it declared still apply to their targets. When
   * absent, the error propagates.
   */
  readonly onError?: (policy: PolicyDecl, err: unknown) => void;
}

export function evalPolicies(
  policies: readonly PolicyDecl[],
  ctx: EvalContext,
  opts?: EvalPoliciesOptions,
  now?: Date,
): Finding[] {
  const escalations = collectEscalations(policies);

  const out: Finding[] = [];
  for (const p of policies) {
    let fs: Finding[];
    try {
      fs = evalPolicy(p, ctx, now);
    } catch (err) {
      if (opts?.onError) {
        opts.onError(p, err);
        continue;
      }
      throw err;
    }
    const esc = escalations.get(p.name);
    out.push(...(esc ? fs.map((f) => ({ ...f, severity: maxSeverity(f.severity, esc) })) : fs));
  }
  return out;
}

/** An escalate rule's target policy/diagnostic key (dotted, usually single-part). */
function escalateTarget(rule: EscalateRule): string {
  return rule.target.join(".");
}

/**
 * The cross-policy escalation map, built from the DECLARED rules (independent
 * of whether a policy's findings evaluate), so a skipped policy can still
 * escalate others. Shared by {@link evalPolicies} and the gate evaluator.
 */
export function collectEscalations(policies: readonly PolicyDecl[]): Map<string, Severity> {
  const escalations = new Map<string, Severity>();
  for (const p of policies) {
    for (const rule of p.rules) {
      if (rule.kind !== "escalate") continue;
      const target = escalateTarget(rule);
      const prev = escalations.get(target);
      escalations.set(target, prev ? maxSeverity(prev, rule.severity) : rule.severity);
    }
  }
  return escalations;
}

/** The diagnostic code every policy finding surfaces under (one code, one axis). */
export const POLICY_DIAGNOSTIC_CODE = "POLICY_VIOLATION";

/**
 * Map a set of policies to engine diagnostics — the shared surface consumed by
 * the CLI and the LSP. Evaluates via {@link evalPolicies} (waivers applied),
 * keeps only **active** findings, and emits a diagnostic per `error`/`warning`
 * finding anchored at the VIOLATION site (`anchorSource`, falling back to the
 * rule span). `advisory` findings are report artifacts, NOT diagnostics (design
 * §4) and are omitted. A finding with no anchorable span at all is dropped
 * (nothing to point at). `opts.onError` skips policies that fail to evaluate.
 */
export function policyDiagnostics(
  policies: readonly PolicyDecl[],
  ctx: EvalContext,
  opts?: EvalPoliciesOptions,
): Diagnostic[] {
  return findingDiagnostics(activeFindings(evalPolicies(policies, ctx, opts)));
}

/**
 * Map a list of findings (already active — waivers/escalations applied) to
 * engine diagnostics. Extracted from {@link policyDiagnostics} so the local and
 * cross-package (`use policy`) evaluation paths share one finding->diagnostic
 * surface: each `error`/`warning` finding anchors at its VIOLATION site
 * (`anchorSource`, falling back to the rule span); `advisory` findings are
 * report artifacts and are dropped; a finding with no anchorable span is dropped.
 * Deduped per (policy, site, message).
 */
export function findingDiagnostics(findings: readonly Finding[]): Diagnostic[] {
  const out: Diagnostic[] = [];
  const seen = new Set<string>();
  for (const f of findings) {
    const severity = diagnosticSeverityOf(f.severity);
    if (!severity) continue; // advisory → not a diagnostic
    const span = f.anchorSource ?? f.source;
    if (!span) continue; // nothing to anchor to
    const message = findingMessage(f);
    // One diagnostic per (policy, site, message) — a rule can't re-flag the same
    // edge, but two identical rules across policies shouldn't double up noise.
    const dedupe = `${f.policy} ${span.file} ${span.start.line}:${span.start.column} ${message}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    // Link back to the flagging rule (when it's a distinct location) so an editor
    // offers a one-click jump from the violation to the policy that forbids it.
    const relatedInformation =
      f.source && !sameSpanStart(f.source, span)
        ? [{ source: f.source, message: `flagged by policy ${f.policy}` }]
        : undefined;
    out.push({ severity, message, source: span, code: POLICY_DIAGNOSTIC_CODE, relatedInformation });
  }
  return out;
}

/** Two spans share a start position (same file + line + column). */
function sameSpanStart(a: SourceSpan, b: SourceSpan): boolean {
  return a.file === b.file && a.start.line === b.start.line && a.start.column === b.start.column;
}

/** Policy severity → diagnostic class; `advisory` (and unknowns below warning)
 *  yield `undefined` — they are not diagnostics. */
function diagnosticSeverityOf(sev: Severity): DiagnosticSeverity | undefined {
  if (sev === "warning") return "warning";
  if (sev === "advisory") return undefined;
  return "error"; // `error` and any unknown severity fail closed as an error
}

// =============================================================================
// policy hygiene lints — catch modeling mistakes IN the policy itself
// =============================================================================

export const POLICY_DEAD_WAIVER_CODE = "POLICY_DEAD_WAIVER";
export const POLICY_REQUIRE_IGNORES_SUBJECT_CODE = "POLICY_REQUIRE_IGNORES_SUBJECT";
export const POLICY_FORBID_IGNORES_NONCARRIERS_CODE = "POLICY_FORBID_IGNORES_NONCARRIERS";

/**
 * Static + evaluated hygiene lints on the policies themselves (distinct from the
 * violation findings). Each anchors at the offending RULE (warning-severity):
 *
 * - **require ignores `this`** (static): a `require <subj>: <obligation>` whose
 *   obligation never references `this` holds identically for every subject — the
 *   author almost certainly meant to relate the obligation to the subject.
 * - **forbid ignores non-carriers** (static): a `forbid` whose arrow sides are
 *   BOTH aspect atoms matches only carriers — an element missing the aspect
 *   slips through silently (an un-zoned middle hop launders the taint). Lints
 *   unless a companion rule in the same policy body covers carrier-lessness.
 * - **dead waiver** (evaluated): an `except` whose selector matches none of the
 *   policy's violations — it grandfathers a violation that no longer exists (or
 *   never did), so it's stale and should be removed.
 *
 * These consume the same evaluator, so a policy that can't evaluate (unsupported
 * feature) yields no dead-waiver lints (its `evalPolicy` throw propagates — the
 * caller decides via its own try/catch). The require-ignores-`this` and
 * forbid-ignores-non-carriers lints are purely structural and never throw.
 */
export function policyLints(policy: PolicyDecl, ctx: EvalContext): Diagnostic[] {
  const lints: Diagnostic[] = [];

  for (const rule of policy.rules) {
    if (rule.kind === "require" && !selectorMentionsThis(rule.obligation)) {
      lints.push({
        severity: "warning",
        code: POLICY_REQUIRE_IGNORES_SUBJECT_CODE,
        message: `require obligation in policy ${policy.name} ignores the subject 'this' — it holds identically for every subject`,
        source: rule.source,
      });
    }
    if (rule.kind === "forbid") {
      const keys = aspectOnlyHopKeys(rule.selector);
      if (keys.length > 0 && !keys.some((key) => policyCoversAbsence(policy, key))) {
        lints.push({
          severity: "warning",
          code: POLICY_FORBID_IGNORES_NONCARRIERS_CODE,
          message:
            `forbid in policy ${policy.name} matches only aspect carriers — an element missing the aspect slips through silently; ` +
            `pair it with a coverage rule like \`forbid module and where (not @@${keys[0]})\``,
          source: rule.source,
        });
      }
    }
  }

  const excepts = policy.rules.filter((r): r is ExceptRule => r.kind === "except");
  if (excepts.length > 0) {
    const findings = evalPolicy(policy, ctx); // all violations, waived + active
    // Except selectors resolve in the policy's OWN context (a module-scoped
    // policy resolves names from its owner chain) — must match evalPolicy.
    const ectx = policyEvalCtx(policy, ctx);
    for (const ex of excepts) {
      const r = evalSelector(ex.selector, ectx);
      const hits =
        r.sort === "node"
          ? findings.some((f) => f.sort === "node" && r.nodes.has(f.anchorKey))
          : findings.some((f) => f.sort === "edge" && f.edge !== undefined && r.edges.has(f.edge));
      if (!hits) {
        lints.push({
          severity: "warning",
          code: POLICY_DEAD_WAIVER_CODE,
          message: `except in policy ${policy.name} waives no violation (dead waiver — remove it or fix its selector)`,
          source: ex.source,
        });
      }
    }
  }

  return lints;
}

/**
 * The aspect keys of every hop whose sides are BOTH aspect atoms, in
 * left-then-right order per hop (so `keys[0]` is a natural companion
 * suggestion). Recurses through combinators to reach nested arrows; a side
 * counts only when it is EXACTLY an aspect atom (the documented, simple
 * heuristic — a composite side already names a non-aspect constraint).
 */
function aspectOnlyHopKeys(sel: Selector): string[] {
  switch (sel.kind) {
    case "arrow": {
      const keys: string[] = [];
      let left = sel.head;
      for (const hop of sel.hops) {
        if (left.kind === "aspect" && hop.rhs.kind === "aspect") {
          keys.push(left.path.join("."), hop.rhs.path.join("."));
        }
        left = hop.rhs;
      }
      return keys;
    }
    case "and":
    case "or":
      return sel.terms.flatMap(aspectOnlyHopKeys);
    case "not":
      return aspectOnlyHopKeys(sel.term);
    case "in":
      return aspectOnlyHopKeys(sel.arg);
    case "extraction":
      return aspectOnlyHopKeys(sel.inner);
    default:
      return [];
  }
}

/**
 * Whether any rule in the policy body covers carrier-lessness for `key`. The
 * heuristic is deliberately SIMPLE and documented: an exact-key `not @@key`
 * term inside a `where (…)` anywhere in the same policy body counts (e.g.
 * `forbid service and where (not @@security.zone)`). Scope-level companions
 * outside the policy body are not seen.
 */
function policyCoversAbsence(policy: PolicyDecl, key: string): boolean {
  return policy.rules.some((rule) => {
    if (rule.kind === "forbid" || rule.kind === "except") return selectorCoversAbsence(rule.selector, key);
    if (rule.kind === "require") {
      return selectorCoversAbsence(rule.subject, key) || selectorCoversAbsence(rule.obligation, key);
    }
    return false;
  });
}

/** Whether a selector contains a `where (… not @@key …)` term (exact key). */
function selectorCoversAbsence(sel: Selector, key: string): boolean {
  switch (sel.kind) {
    case "where":
      return exprHasNotAspect(sel.expr, key);
    case "and":
    case "or":
      return sel.terms.some((t) => selectorCoversAbsence(t, key));
    case "not":
      return selectorCoversAbsence(sel.term, key);
    case "in":
      return selectorCoversAbsence(sel.arg, key);
    case "extraction":
      return selectorCoversAbsence(sel.inner, key);
    case "arrow":
      return selectorCoversAbsence(sel.head, key) || sel.hops.some((h) => selectorCoversAbsence(h.rhs, key));
    default:
      return false;
  }
}

/** Whether an expression contains `not @@key` (a unary `not` over the exact-key
 *  aspect getter), anywhere in its tree. */
function exprHasNotAspect(e: Expr, key: string): boolean {
  switch (e.kind) {
    case "unary":
      if (e.op === "not" && e.operand.kind === "getter" && e.operand.sigil === "@@" && e.operand.path.join(".") === key) {
        return true;
      }
      return exprHasNotAspect(e.operand, key);
    case "binary":
      return exprHasNotAspect(e.left, key) || exprHasNotAspect(e.right, key);
    case "call":
      return e.args.some((a) => exprHasNotAspect(a, key));
    case "aggregate":
    case "exists":
      return selectorCoversAbsence(e.selector, key);
    default:
      return false; // literal / getter / this
  }
}

/** Whether a selector references the bound subject `this` anywhere. */
function selectorMentionsThis(sel: Selector): boolean {
  switch (sel.kind) {
    case "this":
      return true;
    case "in":
      return selectorMentionsThis(sel.arg);
    case "not":
      return selectorMentionsThis(sel.term);
    case "extraction":
      return selectorMentionsThis(sel.inner);
    case "and":
    case "or":
      return sel.terms.some(selectorMentionsThis);
    case "arrow":
      return selectorMentionsThis(sel.head) || sel.hops.some((h) => selectorMentionsThis(h.rhs));
    case "where":
      return exprMentionsThis(sel.expr);
    default:
      return false; // universal / outside / knob / ref / aspect / violating / on
  }
}

/** Whether an expression references the bound subject `this` anywhere. */
function exprMentionsThis(e: Expr): boolean {
  switch (e.kind) {
    case "this":
      return true;
    case "unary":
      return exprMentionsThis(e.operand);
    case "binary":
      return exprMentionsThis(e.left) || exprMentionsThis(e.right);
    case "aggregate":
      return selectorMentionsThis(e.selector);
    case "call":
      return e.args.some(exprMentionsThis);
    case "exists":
      return selectorMentionsThis(e.selector);
    default:
      return false; // literal / getter
  }
}

/** A human-readable finding message for the diagnostic stream. */
function findingMessage(f: Finding): string {
  return f.ruleKind === "forbid"
    ? `${f.anchorKey} is forbidden by policy ${f.policy}`
    : `${f.anchorKey} violates policy ${f.policy} (required relationship missing)`;
}
